import { InputSelectControlField } from '@sequencehq/core-components'
import { useLoadAllItemsQuery } from '@sequencehq/api'
import { dashboardv20240509Client } from '@sequencehq/api/dashboard/v20240509'

export type SelectedProduct = {
  id: string
  name: string
}

type ValidationError = { message: string }

export const ProductField = ({
  productId,
  onChange,
  excludedProductIds = [],
  validationErrors
}: {
  productId: string | undefined
  onChange: (product: SelectedProduct) => void
  /** Hidden from the options (e.g. products already on another line item). */
  excludedProductIds?: string[]
  validationErrors?: ValidationError[]
}) => {
  const { products, productOptions, isLoadingProducts } = useProductOptions()

  // The current selection always stays listed so the control can display it,
  // even if the caller's exclusion list is stale and includes it.
  const selectableOptions = productOptions.filter(
    option =>
      option.value === productId || !excludedProductIds.includes(option.value)
  )

  return (
    <InputSelectControlField
      label="Product"
      data-testid="creditNoteLineItemDrawer.product"
      value={productId ?? ''}
      options={selectableOptions}
      placeholder="Select a product"
      onChange={selectedProductId => {
        const product = products.find(({ id }) => id === selectedProductId)

        if (!product) return

        onChange({ id: product.id, name: product.name })
      }}
      disabled={isLoadingProducts}
      validationErrors={validationErrors}
    />
  )
}

const useProductOptions = () => {
  const { data, hasNextPage, isLoading } = useLoadAllItemsQuery(
    dashboardv20240509Client.getProducts,
    { limit: 100 }
  )

  const products = data?.pages?.flatMap(page => page.items) ?? []

  const productOptions = products
    .map(product => ({
      label: product.name,
      value: product.id
    }))
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
    )

  return {
    products,
    productOptions,
    isLoadingProducts: isLoading || hasNextPage
  }
}
